import { CashflowAggregates, GroupRow } from './cashflowLayout';
import styles from './FinancialsFromBankingV2.module.scss';

/** Chip label for AI-identified entity provenance. */
export const LABEL_ASK_AICA = 'ASK-AICA';

/** Classification buckets presented in the edit-mode dropdown. */
export const CLASSIFICATION_OPTIONS: { label: string; id: number }[] = [
    { label: 'Operating Inflow', id: 3 },
    { label: 'Operating Outflow', id: 4 },
    { label: 'Investing Inflow', id: 6 },
    { label: 'Investing Outflow', id: 7 },
    { label: 'Financing Inflow', id: 1 },
    { label: 'Financing Outflow', id: 5 },
    { label: 'Intracompany', id: -1 },
    { label: 'Bounced', id: 8 },
];

/** Label for entities whose classificationId is not in the 8 selectable buckets. */
export const UNCLASSIFIED_DISPLAY_LABEL = 'Unclassified';

/** Sort field keys for the drill-down drawer's sortable columns — user-visible, since
 *  SortIcon interpolates the field into its accessible name. */
export const DRILL_DOWN_SORT_FIELDS = {
    DATE: 'Txn. Date',
    CREDITS: 'Credits',
    DEBITS: 'Debit',
} as const;

export const DRILL_DOWN_SORT_CLEARED = 'none';

export const DRILL_DOWN_SORT_ANALYTICS_COLUMNS = {
    [DRILL_DOWN_SORT_FIELDS.DATE]: 'date',
    [DRILL_DOWN_SORT_FIELDS.CREDITS]: 'credits',
    [DRILL_DOWN_SORT_FIELDS.DEBITS]: 'debits',
} as const;

/** Payment-method tokens that appear verbatim as bt.rationale but carry no entity identity —
 *  skipped as noise when building fallback chips. Checked case-insensitively. */
export const PAYMENT_METHOD_NOISE = new Set([
    'neft',
    'bank transfer',
    'others',
    'other',
    'chq',
    'cheque',
    'rtgs',
    'cms',
    'payu',
    'paytm',
    'amazon',
    'transfer',
    'remit',
    'atm',
    'imps',
    'clg',
    'ach',
    'cash',
    'tpt',
    'razorpay',
    'card',
    'shiprocket',
    'cod',
    'upi',
    'cashfree',
    'chg',
    'inft',
    'instamojo',
    'pickrr',
    'mobikwik',
    'billdesk',
    'phonepe',
    'ccavenue',
    'mmt',
    'stripe',
    'tpsl',
    'paypal',
    'gateaway',
    'gateway',
    'interbank',
]);

export const EMPTY_AGGREGATES: CashflowAggregates = { opening: {}, netCashflow: {}, closing: {} };

/** Special rows whose label carries a design colour cue. */
export const SPECIAL_LABEL_CLASS: Partial<Record<GroupRow['kind'], string>> = {
    intracompany: styles.IntracompanyLabel,
    unclassified: styles.UnclassifiedLabel,
};

/** Leaves shown per group before "Load more" reveals another page. */
export const LEAF_PAGE_SIZE = 30;

// Canvas font shorthand used for measureText — must match the actual rendered cell/header/chip
// font (Gilroy) for the measured width to be correct.
export const CELL_FONT = '500 14px Gilroy, system-ui, sans-serif';
export const HEAD_FONT = '600 14px Gilroy, system-ui, sans-serif';
export const CHIP_FONT = '600 10px Gilroy, system-ui, sans-serif';
export const CELL_PADDING = 24; // .TableBodyRowItem — 12px each side
export const WIDTH_BUFFER = 10; // ellipsis/rounding slack, and the font may not have loaded yet

/** Clamp so a pathological value can't blow the layout out, nor a sparse column collapse. */
export const PERIOD_MIN = 96;
export const PERIOD_MAX = 210;
export const TOTAL_MIN = 96;
export const TOTAL_MAX = 180;
// Sticky first column — fixed, and excluded from surplus. Kept in sync with the SCSS
// .TableHeadRowItem/.TableBodyRowItem :first-child min/max-width (18.75rem = 300px).
export const NAME_COL_WIDTH = 300;
export const RATIONALE_MIN = 88;
export const RATIONALE_MAX = 160;
// Edit-mode Classification column — fixed width, kept in sync with the SCSS
// .ClassificationHeader width (14rem = 224px at root 16px).
export const CLASSIFICATION_COL_WIDTH = 224;
